import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, BorderStyle, HeadingLevel, PageOrientation } from "docx";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const formatDrug = (drug, getDrugDisplayName) => {
  const dosageDisplay = drug.isPerKg
    ? `${drug.dosePerKg} ${drug.unit || 'mg'}/kg${drug.maxDose ? ` (עד ${drug.maxDose} ${drug.unit || 'mg'})` : ''}`
    : drug.dosage;

  let parts = [];
  if (dosageDisplay) parts.push(stripHtml(dosageDisplay));
  if (drug.frequency) parts.push(`X ${stripHtml(drug.frequency)}`);
  if (drug.duration) parts.push(stripHtml(drug.duration));
  if (drug.howToGive) parts.push(`(${stripHtml(drug.howToGive)})`);
  
  let result = `${stripHtml(getDrugDisplayName(drug.drugId))}`;
  if (parts.length > 0) {
    result += ` - ${parts.join(', ')}`;
  }
  if (drug.comment) {
    result += `\nהערה: ${stripHtml(drug.comment)}`;
  }
  return result;
};

const formatOptions = (options, getDrugDisplayName) => {
  if (!options || options.length === 0) return [new Paragraph({ text: "", bidirectional: true })];
  
  const paragraphs = [];
  options.forEach((opt, optIdx) => {
    if (optIdx > 0) {
      paragraphs.push(new Paragraph({ text: "או (OR):", bidirectional: true, spacing: { before: 120, after: 120 } }));
    }
    
    if (opt.drugs && opt.drugs.length > 0) {
      opt.drugs.forEach((drug, drugIdx) => {
        let prefix = drugIdx > 0 ? "+ " : "";
        const drugText = formatDrug(drug, getDrugDisplayName);
        const lines = drugText.split('\n');
        lines.forEach(line => {
           paragraphs.push(new Paragraph({
             children: [new TextRun({ text: prefix + line, rightToLeft: true })],
             bidirectional: true
           }));
           prefix = "";
        });
      });
    }
  });
  return paragraphs.length > 0 ? paragraphs : [new Paragraph({ text: "", bidirectional: true })];
};

const createListParagraphs = (items) => {
  if (!items || items.length === 0) return [new Paragraph({ text: "", bidirectional: true })];
  return items.map(item => new Paragraph({
    children: [new TextRun({ text: stripHtml(item), rightToLeft: true })],
    bidirectional: true
  }));
};

const createCell = (paragraphs, widthPercent) => {
  return new TableCell({
    children: paragraphs,
    width: {
      size: widthPercent,
      type: WidthType.PERCENTAGE,
    },
    margins: {
      top: 100,
      bottom: 100,
      left: 100,
      right: 100,
    }
  });
};

const createHeaderCell = (text, widthPercent) => {
  return new TableCell({
    children: [
      new Paragraph({
        children: [new TextRun({ text: stripHtml(text), bold: true, rightToLeft: true })],
        alignment: AlignmentType.CENTER,
        bidirectional: true,
      })
    ],
    shading: {
      fill: "E0E0E0",
    },
    width: {
      size: widthPercent,
      type: WidthType.PERCENTAGE,
    },
    margins: {
      top: 100,
      bottom: 100,
      left: 100,
      right: 100,
    }
  });
};

export const exportProtocolToDocx = async (protocol, getDrugDisplayName) => {
  const rows = [];

  // Table Header
  rows.push(new TableRow({
    children: [
      createHeaderCell("אבחנה", 15),
      createHeaderCell("בדיקות אבחנתיות מומלצות", 15),
      createHeaderCell("טיפול קו ראשון", 25),
      createHeaderCell("טיפול אלטרנטיבי", 25),
      createHeaderCell("מצבים מיוחדים / הערות", 10),
      createHeaderCell("משך טיפול", 10),
    ],
    tableHeader: true,
  }));

  // Data Rows
  if (protocol.diagnoses && protocol.diagnoses.length > 0) {
    protocol.diagnoses.forEach(diag => {
      
      const firstLineParagraphs = formatOptions(diag.treatment?.firstLine?.options, getDrugDisplayName);
      const altLineParagraphs = formatOptions(diag.treatment?.alternative?.options, getDrugDisplayName);
      
      const specialCasesText = [...(diag.specialCases || [])];
      if (diag.comment) specialCasesText.push(diag.comment);

      rows.push(new TableRow({
        children: [
          createCell([new Paragraph({ children: [new TextRun({ text: stripHtml(diag.displayName) || "", rightToLeft: true, bold: true })], bidirectional: true })], 15),
          createCell(createListParagraphs(diag.recommendations), 15),
          createCell(firstLineParagraphs, 25),
          createCell(altLineParagraphs, 25),
          createCell(createListParagraphs(specialCasesText), 10),
          createCell(createListParagraphs(diag.treatmentDuration), 10),
        ]
      }));
    });
  } else {
    rows.push(new TableRow({
      children: [
        createCell([new Paragraph({ text: "אין אבחנות", bidirectional: true })], 15),
        createCell([new Paragraph({ text: "", bidirectional: true })], 15),
        createCell([new Paragraph({ text: "", bidirectional: true })], 25),
        createCell([new Paragraph({ text: "", bidirectional: true })], 25),
        createCell([new Paragraph({ text: "", bidirectional: true })], 10),
        createCell([new Paragraph({ text: "", bidirectional: true })], 10),
      ]
    }));
  }

  const table = new Table({
    rows: rows,
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
    },
    visuallyRightToLeft: true,
  });

  const doc = new Document({
    creator: "Mashlom CMS",
    title: stripHtml(protocol.displayName) || "פרוטוקול טיפול",
    sections: [{
      properties: {
        page: {
          size: {
            orientation: PageOrientation.LANDSCAPE,
          },
        },
      },
      children: [
        new Paragraph({
          children: [
            new TextRun({
              text: stripHtml(protocol.displayName) || "פרוטוקול",
              bold: true,
              size: 32, // 16pt
              rightToLeft: true
            })
          ],
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          bidirectional: true,
          spacing: {
            after: 300,
          }
        }),
        ...(protocol.comment ? [
          new Paragraph({
            children: [
              new TextRun({
                text: stripHtml(protocol.comment),
                rightToLeft: true,
                italics: true,
              })
            ],
            bidirectional: true,
            spacing: {
              after: 300,
            }
          })
        ] : []),
        table
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${stripHtml(protocol.displayName) || "protocol"}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
